import { ProductSkusRequestedUseCase } from '../internal/use-cases/ProductSkusRequestedUseCase';
import { ProductRepository } from '../internal/repositories/ProductRepository';
import { Config } from '../internal/domain/entities/Config';
import { ProductGridSettings } from '../internal/domain/entities/ProductGridSettings';
import { ProductFullService } from '../internal/domain/services/ProductFullService';
import { ProductGridService } from '../internal/domain/services/ProductGridService';
import { IProductGridConfig } from '../exported/product-grid-wrapper/ProductGridWrapper';
import { IProductFullConfig } from '../internal/interfaces/IProductFull';
import { IProductDataOptions } from '~api/interfaces/IProductDataOptions';

interface IProductsV3 {
    productIds?: string[];
    productTags?: string[];
    viewableInventoryStatuses?: string[];
}

interface IInventoryStatus {
    inventory_status: number | string;
    isUpdatedInventorySource?: boolean;
    highVelocityQuantity?: number;
}

export interface IInventoryStatuses {
    [key: string]: IInventoryStatus;
}

export class ServiceBusController {
    private configService: Config;
    private productRepository: ProductRepository;
    private productGridSettings: ProductGridSettings;
    private productFullService: ProductFullService;
    private productGridService: ProductGridService;

    constructor(
        configService: Config,
        productRepository: ProductRepository,
        productGridSettings: ProductGridSettings,
        productFullService: ProductFullService,
        productGridService: ProductGridService
    ) {
        this.configService = configService;
        this.productRepository = productRepository;
        this.productGridSettings = productGridSettings;
        this.productFullService = productFullService;
        this.productGridService = productGridService;
    }

    public getProductData = async (options: IProductDataOptions) => {
        if (options.productGridConfig) {
            options.productGridConfig.features = this.configService.config.features;
            const productGridDataParams = await this.productGridService.getProductGridDataParams(
                options.productGridConfig
            );

            return this.productGridService.getProductData(
                productGridDataParams.options,
                productGridDataParams.queryFragments
            );
        } else if (options.productFullConfig) {
            options.productFullConfig.features = this.configService.config.features;
            const productFullDataParams = await this.productFullService.getProductFullDataParams(
                options.productFullConfig
            );

            return this.productFullService.getProductData(
                productFullDataParams.options,
                productFullDataParams.queryFragments
            );
        }

        return this.productRepository.getProductData(options);
    };

    // @todo: Deprecate this query in favor of GET_SKUS
    public getProductSkus = async ({
        categoryIds,
        productIds,
        skuIds,
        query
    }: {
        categoryIds?: string[];
        productIds?: string[];
        skuIds?: string[];
        query?: string;
    }) => {
        const productSkusRequestedUseCase = new ProductSkusRequestedUseCase(
            { categoryIds, productIds, skuIds },
            query
        );

        return productSkusRequestedUseCase.execute();
    };

    // @todo: Promote this function once GET_PRODUCTS has been deprecated
    public getProductsV2 = async (productIds: string[]) => {
        return this.productRepository.getProductsByProductIds(productIds);
    };

    // @todo: Deprecate this when we rebuild analytics, so never
    public getProductsV3 = async ({ productIds = [], productTags = [] }: IProductsV3) => {
        return this.productRepository.getProductsForAnalytics(productIds, productTags);
    };

    public getAnalyticsDataForProductGrid = async (productGridConfig: IProductGridConfig) => {
        productGridConfig.features = this.configService.config.features;

        return this.productGridService.getProductsByGridConfig(productGridConfig);
    };

    public getAnalyticsDataForProductFull = async (productFullConfig: IProductFullConfig) => {
        productFullConfig.features = this.configService.config.features;

        return this.productFullService.getProductByFullConfig(productFullConfig);
    };

    public getSkus = async (skuIds: string[]) => {
        return this.productRepository.getSkuProducts(skuIds);
    };

    public getReviewProduct = async (productId: string) => {
        return this.productRepository.getProductsByProductIds([productId]);
    };

    public getSortbyValue = async () => {
        return this.productGridSettings.sortValue;
    };

    public productRouting = async (payload: {
        currentRoute: string;
        params: string[];
        queryParams: {
            size?: string;
            shade?: string;
        };
    }) => {
        const { queryParams } = payload;
        this.productRepository.setRouterQueryParams(queryParams);
    };
}
